// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
const API = '/api';

// ---------------------------------------------------------------------------
// Auth token provider — set by AuthContext so every API call carries
// the Firebase ID token automatically (when auth is enabled).
// ---------------------------------------------------------------------------
let _tokenGetter: (() => Promise<string | null>) | null = null;

/** Called once from AuthContext to wire up token retrieval. */
export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  _tokenGetter = getter;
}

// ---------------------------------------------------------------------------
// Subscription 402 handler — App.js registers this to show the paywall
// ---------------------------------------------------------------------------
let _on402: (() => void) | null = null;

export function setOn402Handler(handler: () => void) {
  _on402 = handler;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (!_tokenGetter) return {};
  try {
    const token = await _tokenGetter();
    if (token) return { Authorization: `Bearer ${token}` };
  } catch { /* ignore — request proceeds without token */ }
  return {};
}

// Extended Error type that carries structured fields from the server
export interface ApiError extends Error {
  errorType?: string;
  oracleCode?: string;
  detail?: string;
}

async function parseResponse<T>(res: Response): Promise<T> {
  let body: unknown;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    body = await res.json();
  } else {
    const text = await res.text();
    const isHtml = text.trim().startsWith('<') || contentType.includes('text/html');
    body = {
      error: isHtml
        ? `Server returned an unexpected response (HTTP ${res.status}). The service may be temporarily unavailable.`
        : (text || `HTTP ${res.status}`),
      errorType: isHtml ? 'server_error' : 'unknown',
      detail: isHtml
        ? 'An HTML error page was returned instead of a JSON response. Check the Oracle Fusion URL and ensure the BIP service is running.'
        : text,
    };
  }
  if (!res.ok) {
    const b = body as Record<string, unknown>;
    const err = new Error(String(b?.error || `HTTP ${res.status}`)) as ApiError;
    err.errorType  = String(b?.errorType  || 'unknown');
    err.oracleCode = b?.oracleCode ? String(b.oracleCode) : undefined;
    err.detail     = b?.detail     ? String(b.detail)     : undefined;
    // Trigger paywall UI for subscription errors
    if (res.status === 402 && _on402) _on402();
    throw err;
  }
  return body as T;
}

export async function apiPost<T = unknown>(endpoint: string, body: unknown): Promise<T> {
  const extra = await authHeaders();
  const res = await fetch(`${API}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...extra },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

export async function apiGet<T = unknown>(endpoint: string): Promise<T> {
  const extra = await authHeaders();
  const res = await fetch(`${API}${endpoint}`, { headers: extra });
  return parseResponse<T>(res);
}

export async function apiPut<T = unknown>(endpoint: string, body: unknown): Promise<T> {
  const extra = await authHeaders();
  const res = await fetch(`${API}${endpoint}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...extra },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(res);
}

export async function apiDelete<T = unknown>(endpoint: string): Promise<T> {
  const extra = await authHeaders();
  const res = await fetch(`${API}${endpoint}`, { method: 'DELETE', headers: extra });
  return parseResponse<T>(res);
}
